<div class="mx-auto max-w-2xl">
  <!-- Success Header for PAID -->
  @if (isPaid()) {
    <div class="mb-8 text-center">
      <div class="mb-4 flex justify-center">
        <div class="rounded-full bg-primary-100 p-4">
          <lucide-angular
            [img]="checkCircleIcon"
            class="h-16 w-16 text-primary-500"
          />
        </div>
      </div>
      <h1 class="mb-2 text-3xl font-bold text-gray-900">
        {{ 'contribute.success.title' | transloco }}
      </h1>
      <p class="text-lg text-gray-600">
        {{ 'contribute.success.description' | transloco }}
      </p>
    </div>
  }

  <!-- Bank Transfer Pending Header + Recap Card -->
  @if (isBankTransferPending()) {
    <div class="mb-6 overflow-hidden rounded-lg border border-amber-200 bg-amber-50">
      <div class="p-6 text-center">
        <div class="mb-4 flex justify-center">
          <lucide-angular [img]="clockIcon" class="h-12 w-12 text-amber-500" />
        </div>
        <h1 class="mb-2 text-2xl font-bold text-gray-900">
          {{ 'contribute.bankTransfer.pendingTitle' | transloco }}
        </h1>
        <div class="mt-4 inline-block rounded-lg border border-amber-200 bg-white px-6 py-3">
          <p class="font-medium text-gray-900">{{ wizardService.trip()?.title }}</p>
          <p class="text-gray-600">
            <span class="font-semibold text-primary-600">{{ co2CompensatedTonnes() | number:'1.2-2' }} t CO2</span>
            <span class="mx-2 text-gray-400">&middot;</span>
            <span class="font-bold text-gray-900">{{ storedTotalAmount() | number:'1.2-2' }} EUR</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Proforma Invoice Card (Primary CTA) -->
    <div class="mb-6 overflow-hidden rounded-lg border-2 border-primary-200 bg-primary-50">
      <div class="border-b border-primary-200 bg-primary-100/50 p-4">
        <div class="flex items-center gap-2">
          <lucide-angular [img]="fileTextIcon" class="h-5 w-5 text-primary-600" />
          <h3 class="font-semibold text-primary-800">{{ 'contribute.bankTransfer.proformaTitle' | transloco }}</h3>
        </div>
      </div>
      <div class="p-4">
        <p class="mb-4 text-sm text-primary-700">
          {{ 'contribute.bankTransfer.proformaDescription' | transloco }}
        </p>
        <button
          pButton
          icon="pi pi-download"
          class="w-full"
          (click)="downloadProformaInvoice()"
          [disabled]="!hasProformaInvoice()"
        >
          {{ 'contribute.bankTransfer.downloadProforma' | transloco }}
        </button>
        @if (!hasProformaInvoice()) {
          <p class="mt-2 text-center text-xs text-primary-600">
            {{ 'contribute.bankTransfer.proformaGenerating' | transloco }}
          </p>
        }
      </div>
    </div>

    <!-- Bank Details Card (Secondary/Fallback) -->
    @if (bankDetails()) {
      <div class="mb-6 overflow-hidden rounded-lg border border-greyscale-200 bg-white">
        <div class="border-b border-greyscale-200 bg-greyscale-50 p-4">
          <div class="flex items-center gap-2">
            <lucide-angular [img]="buildingIcon" class="h-5 w-5 text-gray-600" />
            <h3 class="font-semibold text-gray-900">{{ 'contribute.bankTransfer.orUseDetails' | transloco }}</h3>
          </div>
        </div>
        <div class="space-y-3 p-4">
          <!-- IBAN -->
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500">IBAN</p>
              <p class="font-mono text-sm font-semibold text-gray-900">{{ bankDetails()?.iban }}</p>
            </div>
            <button
              pButton
              icon="pi pi-copy"
              severity="secondary"
              [text]="true"
              [rounded]="true"
              size="small"
              (click)="copyToClipboard(bankDetails()?.iban || '')"
              aria-label="Copy IBAN"
            ></button>
          </div>

          <!-- BIC -->
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500">BIC/SWIFT</p>
              <p class="font-mono text-sm font-semibold text-gray-900">{{ bankDetails()?.bic }}</p>
            </div>
            <button
              pButton
              icon="pi pi-copy"
              severity="secondary"
              [text]="true"
              [rounded]="true"
              size="small"
              (click)="copyToClipboard(bankDetails()?.bic || '')"
              aria-label="Copy BIC"
            ></button>
          </div>

          <!-- Beneficiary -->
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500">{{ 'contribute.bankTransfer.beneficiary' | transloco }}</p>
              <p class="text-sm font-semibold text-gray-900">{{ bankDetails()?.accountName }}</p>
            </div>
            <button
              pButton
              icon="pi pi-copy"
              severity="secondary"
              [text]="true"
              [rounded]="true"
              size="small"
              (click)="copyToClipboard(bankDetails()?.accountName || '')"
              aria-label="Copy account name"
            ></button>
          </div>

          <!-- Reference - Highlighted -->
          <div class="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-medium text-amber-700">
                  <i class="pi pi-exclamation-triangle mr-1 text-xs"></i>
                  {{ 'contribute.bankTransfer.referenceRequired' | transloco }}
                </p>
                <p class="font-mono text-base font-bold text-amber-800">{{ bankDetails()?.reference }}</p>
              </div>
              <button
                pButton
                icon="pi pi-copy"
                [rounded]="true"
                size="small"
                (click)="copyToClipboard(bankDetails()?.reference || '')"
                aria-label="Copy reference"
              ></button>
            </div>
          </div>

          <!-- Amount -->
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs text-gray-500">{{ 'contribute.bankTransfer.amount' | transloco }}</p>
              <p class="text-lg font-bold text-gray-900">{{ bankDetails()?.amount | number:'1.2-2' }} EUR</p>
            </div>
            <button
              pButton
              icon="pi pi-copy"
              severity="secondary"
              [text]="true"
              [rounded]="true"
              size="small"
              (click)="copyToClipboard((bankDetails()?.amount || 0).toFixed(2))"
              aria-label="Copy amount"
            ></button>
          </div>
        </div>
      </div>
    }

    <!-- Horizontal Steps -->
    <div class="mb-6 rounded-lg border border-greyscale-200 bg-greyscale-50 p-4">
      <div class="flex items-center justify-between gap-2 text-center">
        <div class="flex-1">
          <div class="mx-auto mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-600">1</div>
          <p class="text-xs text-gray-600">{{ 'contribute.bankTransfer.stepShort1' | transloco }}</p>
        </div>
        <lucide-angular [img]="arrowRightIcon" class="h-4 w-4 shrink-0 text-gray-400" />
        <div class="flex-1">
          <div class="mx-auto mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-500">2</div>
          <p class="text-xs text-gray-600">{{ 'contribute.bankTransfer.stepShort2' | transloco }}</p>
        </div>
        <lucide-angular [img]="arrowRightIcon" class="h-4 w-4 shrink-0 text-gray-400" />
        <div class="flex-1">
          <div class="mx-auto mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-500">3</div>
          <p class="text-xs text-gray-600">{{ 'contribute.bankTransfer.stepShort3' | transloco }}</p>
        </div>
      </div>
    </div>
  }

  <!-- Contribution Summary Card (shown for both states) -->
  @if (isPaid()) {
    <div class="mb-6 rounded-lg border border-greyscale-200 bg-white p-6">
      <div class="text-center">
        <p class="mb-2 text-gray-600">{{ 'contribute.success.youCompensated' | transloco }}</p>
        <p class="text-4xl font-bold text-primary-600">
          {{ co2CompensatedTonnes() | number:'1.2-2' }} t CO2
        </p>
        <p class="mt-2 text-gray-500">
          {{ 'contribute.success.for' | transloco }} {{ wizardService.trip()?.title }}
        </p>
        <p class="mt-4 text-2xl font-semibold text-gray-900">
          {{ storedTotalAmount() | number:'1.2-2' }} EUR
          <span class="text-sm font-normal text-gray-500">({{ 'contribute.pricing.inclVat' | transloco }})</span>
        </p>
      </div>
    </div>

    <!-- Impact Equivalences -->
    <div class="mb-6 overflow-hidden rounded-lg border border-greyscale-200 bg-white">
      <div class="border-b border-greyscale-200 p-4">
        <h3 class="font-semibold text-gray-900">{{ 'contribute.success.impactEquivalences' | transloco }}</h3>
      </div>
      <div class="grid grid-cols-3 gap-4 p-4 text-center">
        <div>
          <div class="mb-2 flex justify-center">
            <lucide-angular [img]="treePineIcon" class="h-8 w-8 text-primary-500" />
          </div>
          <p class="text-2xl font-bold text-gray-900">{{ getTreesEquivalent() | number:'1.0-0' }}</p>
          <p class="text-sm text-gray-500">{{ 'contribute.success.trees' | transloco }}</p>
        </div>
        <div>
          <div class="mb-2 flex justify-center">
            <lucide-angular [img]="leafIcon" class="h-8 w-8 text-primary-500" />
          </div>
          <p class="text-2xl font-bold text-gray-900">{{ getKmEquivalent() | number:'1.0-0' }}</p>
          <p class="text-sm text-gray-500">{{ 'contribute.success.kmNotDriven' | transloco }}</p>
        </div>
        <div>
          <div class="mb-2 flex justify-center">
            <lucide-angular [img]="dropletsIcon" class="h-8 w-8 text-primary-500" />
          </div>
          <p class="text-2xl font-bold text-gray-900">{{ getWaterEquivalent() | number:'1.0-0' }}</p>
          <p class="text-sm text-gray-500">{{ 'contribute.success.litersWater' | transloco }}</p>
        </div>
      </div>
    </div>

    <!-- Project Info -->
    <div class="mb-6 overflow-hidden rounded-lg border border-greyscale-200 bg-white">
      <div class="border-b border-greyscale-200 p-4">
        <h3 class="font-semibold text-gray-900">{{ 'contribute.success.supportedProject' | transloco }}</h3>
      </div>
      <div class="flex gap-4 p-4">
        @if (wizardService.selectedProject()?.mainImageUrl) {
          <img
            [src]="wizardService.selectedProject()?.mainImageUrl"
            [alt]="getProjectName()"
            class="h-20 w-20 rounded-lg object-cover"
          />
        }
        <div>
          <h4 class="font-semibold text-gray-900">{{ getProjectName() }}</h4>
          <p class="text-sm text-gray-600">{{ getProjectDescription() }}</p>
        </div>
      </div>
    </div>

  }

  <!-- Action Buttons -->
  <div class="flex flex-col gap-4 sm:flex-row sm:justify-center">
    @if (isPaid() && hasCertificate()) {
      <button
        pButton
        icon="pi pi-download"
        (click)="downloadCertificate()"
      >
        {{ 'contribute.success.downloadCertificate' | transloco }}
      </button>
      <button
        pButton
        icon="pi pi-list"
        severity="secondary"
        (click)="navigateToContributions()"
      >
        {{ 'contribute.success.viewContributions' | transloco }}
      </button>
    }
    @if (isBankTransferPending()) {
      <button
        pButton
        icon="pi pi-eye"
        (click)="navigateToContribution()"
      >
        {{ 'contribute.bankTransfer.viewContribution' | transloco }}
      </button>
    }
    <button
      pButton
      icon="pi pi-calculator"
      severity="secondary"
      (click)="navigateToTravelBoard()"
    >
      {{ 'contribute.success.calculateAnotherTrip' | transloco }}
    </button>
  </div>
</div>
